import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { extractError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";

export default function Register() {
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Workspace created");
      navigate("/dashboard");
    } catch (e2) {
      const msg = extractError(e2);
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="relative min-h-screen grid md:grid-cols-2">
      <div className="grain-overlay" aria-hidden />
      <div className="relative z-10 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <div className="h-8 w-8 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-display font-black">L</div>
            <span className="font-display font-black tracking-tighter">LostDrkC</span>
          </Link>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter mb-2">Create account.</h1>
          <p className="text-muted-foreground text-sm mb-8">Deploy your support cockpit in under a minute.</p>

          <Button data-testid="google-register-btn" variant="outline" className="w-full mb-6 gap-2 h-11" onClick={loginWithGoogle}>
            <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </Button>
          <div className="relative my-6"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border/70" /></div><div className="relative flex justify-center"><span className="bg-background px-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">or with email</span></div></div>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Full name</Label>
              <Input data-testid="register-name" id="name" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Email</Label>
              <Input data-testid="register-email" id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Password</Label>
              <Input data-testid="register-password" id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
            </div>
            {error && <div data-testid="register-error" className="text-xs text-destructive">{error}</div>}
            <Button data-testid="register-submit" type="submit" className="w-full h-11 gap-2" disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Create account <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </form>
          <p className="text-xs text-muted-foreground mt-6">
            Already registered? <Link to="/login" className="text-primary hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
      <div className="hidden md:block relative overflow-hidden border-l border-border/60 bg-card/40">
        <div className="aurora" aria-hidden />
        <div className="absolute inset-0 grid-bg opacity-40" aria-hidden />
        <div className="relative z-10 h-full flex flex-col justify-end p-12">
          <div className="font-mono text-[10px] uppercase tracking-widest text-primary mb-4">// Onboarding</div>
          <div className="font-display font-black tracking-tighter text-5xl leading-[1] mb-4">Ship support like you ship code.</div>
        </div>
      </div>
    </div>
  );
}
